import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Col, DatePicker, Row, Space, Statistic, Table, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import recognitionService, { type AccountReconciliation, type ReconciliationException } from '@/services/financeRecognition.service';
import { amount, apiErrorMessage, contractPath, decimalSub, humanize, jvPath, useRecognitionAccess } from './prepaidDisplay';

const { Title, Text } = Typography;

const CATEGORY_HELP_KEYS: Record<string, string> = {
  DIRECT_GL_POSTING: 'categoryHelpDirectGlPosting',
  UNMATCHED_JOURNAL: 'categoryHelpUnmatchedJournal',
  CANCELLED_JOURNAL: 'categoryHelpCancelledJournal',
  MIGRATION_DIFFERENCE: 'categoryHelpMigrationDifference',
  WRONG_CONTROL_ACCOUNT: 'categoryHelpWrongControlAccount',
  INCOMPLETE_RECOGNITION_POSTING: 'categoryHelpIncompleteRecognitionPosting',
};

const zero = (v: string | null | undefined) => Math.round(Number(v ?? 0) * 100) === 0;

const PrepaidReconciliationPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const access = useRecognitionAccess();
  const [asOf, setAsOf] = useState(dayjs());
  const [runIntegrity, setRunIntegrity] = useState(false);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['recognition', 'reconciliation', asOf.format('YYYY-MM-DD')],
    queryFn: () => recognitionService.reconciliation({ as_of: asOf.format('YYYY-MM-DD') }),
    enabled: access.canReconcile,
  });
  const integrity = useQuery({
    queryKey: ['recognition', 'integrity'],
    queryFn: () => recognitionService.integrity(),
    enabled: access.canReconcile && runIntegrity,
  });

  if (!access.canReconcile) return <Alert type="warning" showIcon style={{ margin: 24 }} message={t('needPermissionReconcile')} />;

  const totals = data?.totals ?? {};
  const explainedTotal = data ? decimalSub(totals.difference, totals.unexplained) : undefined;
  const exceptionsColumns = [
    { title: t('category'), dataIndex: 'category', width: 230, render: (c: string) => <Tag color={c === 'MIGRATION_DIFFERENCE' ? 'blue' : 'orange'}>{humanize(c)}</Tag> },
    { title: t('effectOnDifference'), dataIndex: 'amount', width: 150, align: 'right' as const, render: (v: string) => amount(v) },
    {
      title: t('journal'),
      key: 'jv',
      width: 160,
      render: (_: unknown, r: ReconciliationException) =>
        r.journal_voucher_id ? (
          <Link to={jvPath(r.journal_voucher_id)}>
            {r.voucher_number} {r.voucher_date ? `(${String(r.voucher_date).slice(0, 10)})` : ''}
          </Link>
        ) : (
          '—'
        ),
    },
    {
      title: t('contract'),
      key: 'c',
      width: 150,
      render: (_: unknown, r: ReconciliationException) => (r.recognition_contract_id ? <Link to={contractPath(r.recognition_contract_id)}>{r.recognition_number}</Link> : '—'),
    },
    { title: t('detail'), dataIndex: 'detail' },
  ];

  const summaryCards: Array<{ key: string; label: string; value: string | undefined; warnKey?: boolean }> = [
    { key: 'subledger', label: t('subledgerBalance'), value: totals.subledger },
    { key: 'gl', label: t('glBalance'), value: totals.gl },
    { key: 'difference', label: t('differenceGlMinusSubledger'), value: totals.difference },
    { key: 'explained', label: t('explained'), value: explainedTotal },
    { key: 'unexplained', label: t('unexplained'), value: totals.unexplained, warnKey: true },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/prepaid')}>
            {t('workspace')}
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {t('prepaidSubledgerToGlRecon')}
          </Title>
        </Space>
        <Space>
          <DatePicker value={asOf} allowClear={false} onChange={(v) => v && setAsOf(v)} />
          <Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()}>
            {tc('refresh')}
          </Button>
          <Button icon={<SafetyCertificateOutlined />} loading={integrity.isFetching} onClick={() => (runIntegrity ? integrity.refetch() : setRunIntegrity(true))}>
            {t('runIntegrityCheck')}
          </Button>
        </Space>
      </Space>

      {isError && <Alert type="error" showIcon message={apiErrorMessage(error)} style={{ marginBottom: 12 }} />}

      <Row gutter={12} style={{ marginBottom: 16 }}>
        {summaryCards.map(({ key, label, value, warnKey }) => (
          <Col key={key} xs={12} md={8} lg={4}>
            <Card size="small" loading={isLoading}>
              <Statistic
                title={label}
                value={amount(value)}
                valueStyle={{ fontSize: 18, color: warnKey && !zero(value) ? '#cf1322' : warnKey ? '#389e0d' : undefined }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Table<AccountReconciliation>
        size="small"
        rowKey="account_id"
        loading={isLoading}
        dataSource={data?.accounts ?? []}
        pagination={false}
        expandable={{
          rowExpandable: (r) => r.exceptions.length > 0,
          expandedRowRender: (r) => <Table size="small" rowKey={(e, i) => `${e.category}-${e.journal_voucher_id ?? e.recognition_contract_id ?? i}`} pagination={r.exceptions.length > 20 ? { pageSize: 20 } : false} dataSource={r.exceptions} columns={exceptionsColumns} />,
        }}
        columns={[
          {
            title: t('prepaidAccount'),
            key: 'acct',
            render: (_: unknown, r) => (
              <Space>
                <Link to={`/fin/prepaid/contracts?prepaid_asset_account_id=${r.account_id}`}>
                  {r.account_code} - {r.account_name}
                </Link>
                {r.recognition_controlled ? <Tag color="blue">{t('controlled')}</Tag> : <Tag>{t('notControlled')}</Tag>}
              </Space>
            ),
          },
          { title: t('contracts'), dataIndex: 'contract_count', width: 90, align: 'right' },
          { title: t('subledger'), dataIndex: 'subledger_balance', width: 140, align: 'right', render: (v: string) => amount(v) },
          { title: t('gl'), dataIndex: 'gl_balance', width: 140, align: 'right', render: (v: string) => amount(v) },
          { title: t('difference'), dataIndex: 'difference', width: 140, align: 'right', render: (v: string) => amount(v) },
          { title: t('explained'), dataIndex: 'explained', width: 140, align: 'right', render: (v: string) => amount(v) },
          {
            title: t('unexplained'),
            dataIndex: 'unexplained',
            width: 140,
            align: 'right',
            render: (v: string) => (zero(v) ? <Text type="success">{amount(v)}</Text> : <Text type="danger">{amount(v)}</Text>),
          },
          { title: t('exceptions'), key: 'ex', width: 100, align: 'right', render: (_: unknown, r) => r.exceptions.length },
        ]}
      />

      <Card size="small" title={t('exceptionCategories')} style={{ marginTop: 16 }}>
        <Space direction="vertical" size={2}>
          {Object.entries(CATEGORY_HELP_KEYS).map(([k, key]) => (
            <Text key={k}>
              <Tag>{humanize(k)}</Tag>
              {t(key)}
            </Text>
          ))}
        </Space>
      </Card>

      {runIntegrity && (
        <Card size="small" title={t('subledgerIntegrityCheck')} style={{ marginTop: 16 }} loading={integrity.isLoading}>
          {integrity.isError && <Alert type="error" showIcon message={apiErrorMessage(integrity.error)} />}
          {integrity.data && (
            <>
              <Text type={integrity.data.issues.length ? 'danger' : 'success'}>
                {t('contractsCheckedIssues', { checked: integrity.data.checked, issues: integrity.data.issues.length })}
              </Text>
              {integrity.data.issues.length > 0 && (
                <Table
                  style={{ marginTop: 8 }}
                  size="small"
                  rowKey={(r, i) => `${r.recognition_contract_id}-${r.check}-${i}`}
                  dataSource={integrity.data.issues}
                  pagination={{ pageSize: 20 }}
                  columns={[
                    { title: t('contract'), dataIndex: 'recognition_number', render: (v: string, r) => <Link to={contractPath(r.recognition_contract_id)}>{v}</Link> },
                    { title: t('check'), dataIndex: 'check', render: (v: string) => humanize(v) },
                    { title: t('expected'), dataIndex: 'expected', align: 'right', render: (v: string) => amount(v) },
                    { title: t('actual'), dataIndex: 'actual', align: 'right', render: (v: string) => amount(v) },
                    { title: t('detail'), dataIndex: 'detail' },
                  ]}
                />
              )}
            </>
          )}
        </Card>
      )}
    </div>
  );
};

export default PrepaidReconciliationPage;
